'use client'

import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	XAxis,
	YAxis,
	Brush,
} from 'recharts'
import { useState, useMemo } from 'react'
import { Button } from '@/common/ui/button'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	ChartLegendContent,
} from '@/common/ui/chart'

interface DataKeys {
	[key: string]: {
		label: string
		color: string
	}
}

interface LineChartGraphProps {
	data: Array<{
		week: string
		[key: string]: string | number
	}>
	dataKeys: DataKeys
	title?: string
	description?: string
	enableSliding?: boolean
	rangeOptions?: Array<{ label: string; days: number | null }>
	yAxisFormatter?: (value: number) => string
	tooltipFormatter?: (value: number) => string
	tooltipLabelFormatter?: (label: string) => string
}

// Default range options
const DEFAULT_RANGE_OPTIONS = [
	{ label: '7D', days: 7 },
	{ label: '1M', days: 30 },
	{ label: '3M', days: 90 },
	{ label: '6M', days: 180 },
	{ label: '1Y', days: 365 },
	{ label: 'All', days: null },
]

export function LineChartGraph({
	data,
	dataKeys,
	title,
	description,
	enableSliding = false,
	rangeOptions = DEFAULT_RANGE_OPTIONS,
	yAxisFormatter = (value) => `${(value / 1000).toLocaleString()}k`,
	tooltipFormatter = (value) => `฿${Number(value).toLocaleString()}`,
	tooltipLabelFormatter = (label) => `Date: ${label}`,
}: LineChartGraphProps) {
	const [selectedRange, setSelectedRange] = useState<string>('All')
	const [brushRange, setBrushRange] = useState<{
		startIndex?: number
		endIndex?: number
	}>({})

	// Filter data based on selected range
	const filteredData = useMemo(() => {
		if (!enableSliding || selectedRange === 'All') return data

		const selectedOption = rangeOptions.find(
			(opt) => opt.label === selectedRange,
		)
		if (!selectedOption?.days) return data

		// Get the last N days of data
		return data.slice(-selectedOption.days)
	}, [data, enableSliding, selectedRange, rangeOptions])

	const handleRangeSelect = (range: string) => {
		setSelectedRange(range)
		setBrushRange({}) // Reset brush when changing range
	}

	const handleBrushChange = (brushData: {
		startIndex?: number
		endIndex?: number
	}) => {
		if (brushData) {
			setBrushRange({
				startIndex: brushData.startIndex,
				endIndex: brushData.endIndex,
			})
		}
	}

	return (
		<div className="rounded-2xl bg-transparent">
			<div className="p-6 pb-2">
				<div className="flex items-center justify-between">
					<div className="space-y-1">
						<h3 className="text-2xl font-bold tracking-tight">{title}</h3>
						<p className="text-sm text-muted-foreground">{description}</p>
					</div>
					{enableSliding && (
						<div className="flex gap-2">
							{rangeOptions.map((option) => (
								<Button
									key={option.label}
									variant={
										selectedRange === option.label ? 'default' : 'outline'
									}
									size="sm"
									onClick={() => handleRangeSelect(option.label)}
									className="h-8 px-3 text-xs font-medium shadow-sm transition-all hover:scale-105"
								>
									{option.label}
								</Button>
							))}
						</div>
					)}
				</div>
			</div>
			<div className="px-6 pb-6">
				<ChartContainer config={dataKeys}>
					<LineChart
						data={filteredData}
						margin={{
							left: 12,
							right: 12,
							top: 12,
							bottom: enableSliding ? 60 : 12,
						}}
					>
						<CartesianGrid vertical={false} />
						<XAxis
							dataKey="week"
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							domain={
								brushRange.startIndex !== undefined
									? ['dataMin', 'dataMax']
									: undefined
							}
						/>
						<YAxis
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							tickFormatter={yAxisFormatter}
						/>
						<ChartTooltip
							content={
								<ChartTooltipContent
									formatter={tooltipFormatter as never}
									labelFormatter={tooltipLabelFormatter}
								/>
							}
							cursor={{
								stroke: '#888',
								strokeWidth: 1,
								strokeDasharray: '5 5',
							}}
						/>
						{Object.keys(dataKeys).map((key) => (
							<Line
								key={key}
								dataKey={key}
								type="monotone"
								stroke={`var(--color-${key})`}
								strokeWidth={2}
								dot={false}
								activeDot={{
									r: 4,
									stroke: `var(--color-${key})`,
									strokeWidth: 2,
									fill: 'white',
								}}
							/>
						))}
						<Legend content={<ChartLegendContent />} />

						{/* Brush for sliding/zooming functionality */}
						{enableSliding && (
							<Brush
								dataKey="week"
								height={30}
								stroke="#8884d8"
								fill="rgba(136, 132, 216, 0.1)"
								onChange={handleBrushChange}
								startIndex={brushRange.startIndex}
								endIndex={brushRange.endIndex}
							/>
						)}
					</LineChart>
				</ChartContainer>
			</div>
		</div>
	)
}
